import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { PartnerResponse } from '@interfaces/partners/responses/partner.response';
import { TableComponent } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs/operators';

/**
 * Listagem de parceiros comerciais (card 2916). Menu: Negociação › Comercial › Parceiros desde o
 * card 3683; antes ficava em Gerenciamento › Parceiros.
 *
 * Antes desta tela o parceiro só existia para a Legatus dentro do filtro do board de Deals:
 * não havia onde ver quem são, cadastrar um à mão ou tirar o acesso de quem saiu.
 */
@Component({
  selector: 'app-partner-list',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './partner-list.component.html',
  styleUrls: ['./partner-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerListComponent implements OnInit {
  readonly permissions = Permissions.Partner;

  readonly home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: 'Dashboard',
  };
  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Parceiros', routerLink: '/dashboard/partner/list' },
  ];

  readonly partners = signal<PartnerResponse[]>([]);
  /** Começa ligado: a primeira carga sai do `ngOnInit`, e a tabela abre no esqueleto. */
  readonly loading = signal(true);
  /** A primeira página já chegou (troca o esqueleto do contador pelo número). */
  readonly loaded = signal(false);
  readonly total = signal(0);
  readonly pageSize = signal(10);

  /**
   * A rota exige apenas `PARTNER_LIST`, mas editar e inativar exigem `PARTNER_UPDATE`.
   *
   * Sem esta separação, quem tem só a listagem veria as ações, clicaria, confirmaria a
   * inativação — e levaria 403. Oferecer um botão que o backend nega é pior do que não
   * oferecer.
   */
  readonly podeEditar = signal(false);

  /**
   * Colunas da listagem.
   *
   * Sem contagem de deals: `PartnerDeal` e `Partner` vivem em `DbContext` diferentes no
   * backend, e a decisão do card foi deixar a listagem sair inteira da Identity. O dia em
   * que a métrica entrar, ela custa uma segunda consulta — não um `join`.
   */
  readonly columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({ field: 'sequentialId', header: 'ID' }),
    new ColumnDefinition({ field: 'name', header: 'Nome' }),
    new ColumnDefinition({ field: 'email', header: 'E-mail' }),
    new ColumnDefinition({ field: 'phoneNumber', header: 'Telefone' }),
    // Card 3683: quem indicou (link de indicação do Indique e ganhe). Template próprio para
    // mostrar o usuário do parceiro em segundo plano e o "—" de quem não veio por indicação.
    new ColumnDefinition({
      field: 'referredByPartnerName',
      header: 'Indicado por',
      template: 'referredByPartnerName',
    }),
    new ColumnDefinition({ field: 'active', header: 'Situação', template: 'active' }),
    new ColumnDefinition({ field: 'createdAt', header: 'Cadastro', format: 'date' }),
  ];

  /** A tabela, para resetar o paginador quando a busca muda. */
  @ViewChild('table') private table?: TableComponent<PartnerResponse>;

  private page = 1;
  private filtro = '';

  /** Verdadeiro enquanto a busca reseta o paginador, para ignorar o evento que isso gera. */
  private resetandoPaginacao = false;

  constructor(
    private readonly partnersService: PartnersHttpService,
    private readonly toast: ToastHelperService,
    private readonly confirmationService: ConfirmationService,
    private readonly permissionsService: PermissionsService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.carregar();

    this.permissionsService
      .hasPermission(this.permissions.UPDATE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pode => {
        this.podeEditar.set(pode);
        this.cdr.markForCheck();
      });
  }

  /** Vai para o cadastro de um parceiro novo. */
  novoParceiro(): void {
    this.router.navigate(['/dashboard/partner/register']);
  }

  /**
   * Menu de ações da linha.
   *
   * "Inativar" e "Ativar" são a mesma ação em direções opostas, e por isso um item só: dois
   * itens sempre visíveis deixariam um deles inerte em toda linha.
   */
  acoesDaLinha(parceiro: PartnerResponse): MenuItem[] {
    // Cinto de segurança: quem esconde a coluna é o binding condicional no template, já
    // que o `app-table` olha se a função está bindada e não o que ela devolve.
    if (!this.podeEditar()) return [];

    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.router.navigate(['/dashboard/partner/register', parceiro.id]),
      },
      {
        label: parceiro.active ? 'Inativar' : 'Ativar',
        icon: parceiro.active ? 'pi pi-ban' : 'pi pi-check-circle',
        command: () => this.confirmarMudancaDeEstado(parceiro),
      },
    ];
  }

  /** Página nova na paginação da tabela. */
  onPaginacao(evento: PaginatorState): void {
    // Ignora o evento que a própria busca provocou ao resetar o paginador — senão são
    // dois GET por busca. Mesma trava do `company-list`.
    if (this.resetandoPaginacao) return;

    this.page = (evento.page ?? 0) + 1;
    this.pageSize.set(evento.rows ?? 10);
    this.carregar();
  }

  /**
   * Busca da própria tabela — o backend é quem filtra, então volta para a primeira página.
   *
   * O paginador do `app-table` também precisa ser resetado: sem isso, buscando a partir da
   * página 3, o backend devolve a página 1 e o paginador continua destacando a 3.
   */
  onBusca(termo: string): void {
    this.filtro = termo ?? '';
    this.page = 1;

    // O `changePage(0)` do PrimeNG emite mesmo quando já estamos na primeira página, e o
    // evento cairia em `onPaginacao` disparando um segundo GET. Quem carrega é o
    // `carregar()` abaixo, sempre — inclusive quando a lista está vazia e o paginador
    // não chega a emitir nada.
    this.resetandoPaginacao = true;
    this.table?.resetPagination();
    this.resetandoPaginacao = false;

    this.carregar();
  }

  /**
   * Pede confirmação antes de mexer no acesso do parceiro.
   *
   * Inativar derruba o login dele; é o tipo de ação que não pode acontecer por um clique
   * errado no menu da linha.
   */
  private confirmarMudancaDeEstado(parceiro: PartnerResponse): void {
    const titulo = parceiro.active ? `Inativar ${parceiro.name}?` : `Reativar ${parceiro.name}?`;

    const descricao = parceiro.active
      ? 'Ele deixa de entrar na plataforma e de registrar deals novos. Os deals que ele já registrou continuam como estão.'
      : 'Ele volta a entrar na plataforma e a registrar deals.';

    this.confirmationService
      .confirm(titulo, descricao)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmado => {
        if (confirmado) this.mudarEstado(parceiro);
      });
  }

  /**
   * Persiste o novo estado e recarrega a página atual.
   *
   * A tabela continua montada durante o recarregamento (o esqueleto só aparece sem itens), então a
   * linha é atualizada na hora com o estado novo: sem isso, o parceiro recém-inativado ainda
   * apareceria como "Ativo" até a resposta do `carregar()`. O `loading` não é desligado aqui no
   * caminho feliz, porque o `carregar()` o religa e desliga; só o caminho de erro precisa fazê-lo.
   */
  private mudarEstado(parceiro: PartnerResponse): void {
    this.loading.set(true);

    this.partnersService
      .setPartnerActive(parceiro.id, !parceiro.active)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            parceiro.active ? 'Parceiro inativado' : 'Parceiro reativado',
            ''
          );
          this.partners.update(lista =>
            lista.map(p => (p.id === parceiro.id ? { ...p, active: !parceiro.active } : p))
          );
          this.carregar();
        },
        error: erro => {
          this.loading.set(false);
          this.mostrarErro('Não foi possível mudar o estado do parceiro', erro);
        },
      });
  }

  /** Carrega a página atual da listagem. */
  private carregar(): void {
    this.loading.set(true);

    this.partnersService
      .getPartners(this.page, this.pageSize(), this.filtro)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loaded.set(true);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: pagina => {
          this.partners.set(pagina?.data ?? []);
          this.total.set(pagina?.total ?? 0);
        },
        error: erro => this.mostrarErro('Não foi possível carregar os parceiros', erro),
      });
  }

  /**
   * Mostra a mensagem que o backend mandou, e só cai no texto genérico quando não há uma.
   *
   * A mensagem de negócio é a que diz o que fazer ("já existe um parceiro com este
   * e-mail"); o texto genérico serve para falha técnica.
   */
  private mostrarErro(titulo: string, erro: { errors?: { message: string }[] }): void {
    this.toast.showToastError(titulo, erro?.errors?.length ? erro.errors[0].message : '');
  }
}
